import { useState } from 'react';
import { callDeepLink, compareWithAppVersion } from '@croquiscom/web2app';
import { useRouter } from 'next/router';

import { useAppConfig } from '@common/app-config';
import { SwiperClass } from '@common/carousel/swiper';
import { useOnceReveal } from '@common/hooks';
import {
  GetPdpIntegratedData_pdp_short_form_contents_content_list,
  useCatalogProductIdParams,
  useGetPdpIntegratedData,
  usePdpBaseInfo,
} from '@domains/pdp-common';

import { GetNativeDescription_pdp_description_content_short_form_content_content_list } from '../../product-description/graphql/native-description_types.2';
import { toShortClipContentList } from '../utils/mapper';

interface ShortClipBaseProps {
  productId: string;
  shop: { name: string | null; typical_image_url: string | null };
  short_form_contents:
    | GetPdpIntegratedData_pdp_short_form_contents_content_list[]
    | GetNativeDescription_pdp_description_content_short_form_content_content_list[];
}

export const useShortClipBase = ({ productId, shop, short_form_contents }: ShortClipBaseProps) => {
  const [firstOpen, setFirstOpen] = useState<boolean>(true);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const { targetRef, isRevealed } = useOnceReveal();
  const router = useRouter();
  const { isZigzagApp } = useAppConfig();

  const handleShortClipClick = (videoId: string) => {
    if (!firstOpen) {
      setFirstOpen(true);
    }

    const searchParams = new URLSearchParams({
      video_id: videoId,
      shop_name: shop?.name || '',
      profile_url: shop?.typical_image_url || '',
      is_opened: String(firstOpen),
    });
    const path = `/shortform/${productId}?${searchParams.toString()}`;
    if (isZigzagApp) {
      const url = encodeURIComponent(`${window.location.origin}${path}`);
      const background_color = compareWithAppVersion('7.47.0') ? { background_color: '000000' } : {};

      callDeepLink('/full_screen_simple_browser')({
        url,
        features: 'hp',
        ...background_color,
      });
    } else {
      router.push(path);
    }
  };

  const handleActiveIndexChange = (swiper: SwiperClass) => {
    const currentIndex = swiper.realIndex;
    setActiveIndex(currentIndex);
  };

  return {
    isHide: !short_form_contents?.length,
    isSingleList: Boolean(short_form_contents?.length === 1),
    targetRef,
    isRevealed,
    handleShortClipClick,
    activeIndex,
    handleActiveIndexChange,
    productId,
    shortClipContentList: toShortClipContentList(short_form_contents || []),
  };
};

export const useShortClipService = () => {
  const params = useCatalogProductIdParams();
  const { product, shop } = usePdpBaseInfo(params);
  const { short_form_contents } = useGetPdpIntegratedData({ catalog_product_id: product.id });

  return useShortClipBase({
    productId: product.id,
    shop,
    short_form_contents: short_form_contents ?? [],
  });
};
